import { generateFakeNlhePlayers } from "./generate-fake-nlhe-players";
import { getMovesFromSequence } from "./get-moves-from-sequence";
import { getStreetFromSequence } from "./get-street-from-sequence";

import { Street, StreetValues } from "@acepoker/contract";
import type { PreviewTablePlayer, PreviewTableConfig } from "./../types";
import type { Drill, DrillSequence } from "@acepoker/contract";
import type { PlayerPosition } from "@acepoker/contract/acepoker";

import {
  CommunityCardsCountMap,
  confirmCardsCount,
} from "@/components/drills/helpers/confirm-cards-count";

export function generateDrillPreviewSettings(
  drill: Pick<Drill, "gaming_configuration" | "max_players" | "data">,
  sequence: {
    action: string;
    amount?: number;
  }[],
): PreviewTableConfig {
  const drillConfig = (
    Array.isArray(drill.data) ? drill.data[0] : drill.data
  ) as Record<string, string | Array<string>>;
  const isDisabled = !drillConfig?.street;
  const streets = StreetValues;
  let streetNumber = 0;

  const withStr = drill.gaming_configuration.blinds.length === 3;
  const lastActPosition: PlayerPosition = withStr ? "STR" : "BB";

  const seats: PreviewTablePlayer[] = generateFakeNlhePlayers(drill);

  if (isDisabled) {
    return {
      gameConfig: {
        max_players: drill.max_players,
        gaming_configuration: drill.gaming_configuration,
      },
      street: Street.Preflop,
      seats,
      disabled: true,
    };
  }

  // lastActIndex - to shift actions to the right position
  let lastActIndex = seats.findIndex(
    (seat) => seat.position === lastActPosition,
  );

  // preflop defaults
  seats[1]!.chips = 0.5;
  seats[2]!.chips = 1;
  if (withStr) seats[3]!.chips = 2;

  if (sequence.length === 0) {
    seats[lastActIndex + 1]!.isPlayer = true;
  } else {
    // raiseCount to track 2-bet, 3-bet, etc.
    // lastBet to track chips amount
    // lastToActPosition to track the last position of the street
    let raiseCount = 0;
    let lastBet = 2;
    let lastToActPosition: PlayerPosition | null = lastActPosition;

    sequence.forEach(({ action }) => {
      const seat = getNextActiveSeat(seats, lastActIndex);
      lastActIndex = seat.seat_no;

      seat.isPlayer = false;
      seat.folded = action === "fold";

      if (action !== "fold") {
        if (action === "bet" || action === "raise") {
          const prevSeat = getPrevActiveSeat(seats, lastActIndex);
          lastToActPosition = prevSeat.position;
        }

        if (action === "bet") {
          raiseCount = 1;
          lastBet = 2;
        }

        if (action === "raise") {
          raiseCount++;
          lastBet = Math.pow(
            2,
            streets[streetNumber] === Street.Preflop
              ? raiseCount + 1
              : raiseCount,
          );
        }

        addSeatsTags(seat, drillConfig, action, raiseCount);
        addSeatsChips(seat, drillConfig, action, lastBet);
      }

      // end of street
      if (action !== "raise" && seat.position === lastToActPosition) {
        const lastActiveSeat = getPrevActiveSeat(seats, 1);
        lastToActPosition = lastActiveSeat.position;
        lastActIndex = lastActiveSeat.seat_no;

        resetTagsAndChips(seats);

        raiseCount = 0;
        lastBet = 0;
        streetNumber++;
      }
    });

    const seat = getNextActiveSeat(seats, lastActIndex);
    seat.isPlayer = true;
  }

  return {
    gameConfig: {
      max_players: drill.max_players,
      gaming_configuration: drill.gaming_configuration,
    },
    street: streets[streetNumber]!,
    seats,
  };
}

function getNextActiveSeat(
  seats: PreviewTablePlayer[],
  currentSeatIndex: number,
): PreviewTablePlayer {
  const nextIndex = (currentSeatIndex + 1) % seats.length;
  const nextSeat = seats[nextIndex]!;
  return nextSeat.folded ? getNextActiveSeat(seats, nextIndex) : nextSeat;
}

function getPrevActiveSeat(
  seats: PreviewTablePlayer[],
  currentSeatIndex: number,
): PreviewTablePlayer {
  const prevIndex = (currentSeatIndex - 1 + seats.length) % seats.length;
  const prevSeat = seats[prevIndex]!;
  return prevSeat.folded ? getPrevActiveSeat(seats, prevIndex) : prevSeat;
}

function resetTagsAndChips(seats: PreviewTablePlayer[]) {
  seats.forEach((el) => {
    el.chips = 0;
    el.tag = "";
  });
}

function addSeatsChips(
  seat: PreviewTablePlayer,
  drillConfig: Record<string, string | Array<string>>,
  action: string,
  lastBet: number,
) {
  if (action === "fold") return;

  seat.chips = lastBet;

  if (action === "raise" && drillConfig.scenario_type === "vs_iso") {
    seat.chips = 6;
  }
}

function addSeatsTags(
  seat: PreviewTablePlayer,
  drillConfig: Record<string, string | Array<string>>,
  action: string,
  raiseCount: number,
) {
  if (action === "fold") return;

  seat.tag = action;

  const raisesWithoutBet =
    drillConfig.street !== Street.Preflop ? raiseCount - 1 : raiseCount;

  if (action === "raise") {
    if (raisesWithoutBet === 2) {
      seat.tag = "re-raise";
    }

    if (drillConfig.scenario_type === "vs_iso") {
      seat.tag = "ISO";
    }
    if (drillConfig.scenario_type === "vs_3bet") {
      seat.tag = "3-bet";
    }
  }

  if (
    drillConfig.street === Street.Preflop &&
    action === "call" &&
    !raiseCount
  ) {
    seat.tag = `limp`;
  }
}

export function generateCustomDrillPreviewSettings(
  drill: Pick<Drill, "gaming_configuration" | "max_players">,
  sequence: DrillSequence["sequence"],
) {
  const isDisabled = !drill;

  const withStr = drill.gaming_configuration.blinds.length === 3;
  const lastActPosition: PlayerPosition = withStr ? "STR" : "BB";

  const seats: PreviewTablePlayer[] = generateFakeNlhePlayers(drill);

  // TODO: check design
  if (isDisabled) {
    return {
      gameConfig: {
        max_players: drill.max_players,
        gaming_configuration: drill.gaming_configuration,
      },
      street: Street.Preflop,
      seats,
      disabled: true,
    };
  }

  // lastActIndex - to shift actions to the right position
  let lastActIndex = seats.findIndex(
    (seat) => seat.position === lastActPosition,
  );

  const street = getStreetFromSequence(sequence);
  const moves = getMovesFromSequence(sequence);

  moves.forEach((move) => {
    const seat = seats.find((el) => el.position === move.position);
    lastActIndex = seat.seat_no;

    seat.isPlayer = false;
    seat.folded = move.action === "fold";
  });

  const seat = getNextActiveSeat(seats, lastActIndex);
  seat.isPlayer = true;

  const result = {
    gameConfig: {
      max_players: drill.max_players,
      gaming_configuration: drill.gaming_configuration,
    },
    street,
    seats,
    tableCards: sequence
      .filter((el) => el.kind === "round")
      .flatMap((el) =>
        confirmCardsCount(
          CommunityCardsCountMap[el.round],
          (el.cards.match(/.{1,2}/g) ?? [])
        ),
      ),
  };

  return result;
}
